import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShoppingCart, PoundSterling, Eye, Plus, Check, AlertTriangle, Package, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Product } from "@/data/products";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { WishlistManager } from "@/components/advanced/WishlistManager";
import { QuickViewModal } from "@/components/ui/quick-view-modal";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  
  // Lazy loading with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    
    if (imageRef.current) {
      observer.observe(imageRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  // Calculate fallback subscription price as 85% of regular price if not set
  const basePrice = product.price || 0;
  const baseSubscriptionPrice = product.subscription_price && product.subscription_price > 0 
    ? product.subscription_price 
    : basePrice * 0.85;
  
  const packSizes = product.packSizes && product.packSizes.length > 0 
    ? product.packSizes 
    : [{ size: 30, price: basePrice, subscription_price: baseSubscriptionPrice }];
  const [selectedPack, setSelectedPack] = useState(packSizes[0]);
  const [showQuickView, setShowQuickView] = useState(false);
  const { addToCart } = useCart();
  
  const isPreorder = !!product.isPreorder;
  const releaseDateLabel = product.preorderReleaseDate
    ? new Date(product.preorderReleaseDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : null;

  // Real stock data from database (ignored when preorder)
  const stockQuantity = product.stockQuantity ?? 100;
  const lowStockThreshold = product.lowStockThreshold ?? 10;
  const isLowStock = !isPreorder && stockQuantity <= lowStockThreshold && stockQuantity > 0;
  const isOutOfStock = !isPreorder && stockQuantity === 0;
  const isBestseller = !isPreorder && product.category === 'focus';
  
  const [justAdded, setJustAdded] = useState(false);
  
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) return;
    addToCart(product);
    setJustAdded(true);
    toast.success(`${product.name} added to cart!`);
    setTimeout(() => setJustAdded(false), 2000);
  };

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) return;
    const packSize = packSizes[0]?.size || 12;
    addToCart(product, packSize, false);
    setJustAdded(true);
    toast.success(`${product.name} added to cart!`);
    setTimeout(() => setJustAdded(false), 2000);
  };

  const handleQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowQuickView(true);
  };

  // Stock / preorder indicator component
  const StockIndicator = () => {
    if (isPreorder) {
      return (
        <Badge className="absolute top-2 left-2 z-10 bg-solr-nebula-purple hover:bg-solr-nebula-purple/90 flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Coming {releaseDateLabel ?? 'Soon'}
        </Badge>
      );
    }
    if (isOutOfStock) {
      return (
        <Badge variant="destructive" className="absolute top-2 left-2 z-10 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Out of Stock
        </Badge>
      );
    }
    if (isLowStock) {
      return (
        <Badge className="absolute top-2 left-2 z-10 bg-amber-500 hover:bg-amber-600 flex items-center gap-1">
          <Package className="w-3 h-3" />
          Only {stockQuantity} left
        </Badge>
      );
    }
    if (stockQuantity <= 20) {
      return (
        <Badge variant="secondary" className="absolute top-2 left-2 z-10 flex items-center gap-1 text-xs">
          <Package className="w-3 h-3" />
          {stockQuantity} in stock
        </Badge>
      );
    }
    return null;
  };
  
  return (
    <div 
      className={`cosmic-card group transition-all duration-300 hover:cosmic-shadow ${isOutOfStock ? 'opacity-75' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Neural connections */}
      <div className={`absolute top-1/4 left-0 w-0 neural-connection ${isHovered ? 'w-full transition-all duration-700' : ''}`} />
      <div className={`absolute top-3/4 left-0 w-0 neural-connection ${isHovered ? 'w-full transition-all duration-700 delay-100' : ''}`} />
      
      <Link to={`/shop/${product.id}`} className="block p-4 sm:p-5 md:p-6">
        <div 
          ref={imageRef}
          className="aspect-square rounded-lg overflow-hidden mb-3 sm:mb-4 bg-solr-nebula-purple/5 flex items-center justify-center relative group/image p-2"
        >
          {/* Stock Indicator */}
          <StockIndicator />
          
          {/* Bestseller badge */}
          {isBestseller && !isOutOfStock && !isLowStock && (
            <div className="absolute top-2 left-2 bg-solr-synapse-gold text-solr-space-darker px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold z-10">
              Bestseller
            </div>
          )}
          
          {/* Lazy loaded image with skeleton */}
          {isInView ? (
            <>
              {!imageLoaded && (
                <div className="absolute inset-0 bg-muted animate-pulse rounded-lg" />
              )}
              <img 
                src={product.image} 
                alt={product.name} 
                className={`object-contain w-full h-full max-h-full transition-all duration-500 group-hover:scale-105 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
              />
            </>
          ) : (
            <div className="absolute inset-0 bg-muted animate-pulse rounded-lg" />
          )}
          
          {/* Quick Add Button - Floating */}
          {!isOutOfStock && (
            <button
              onClick={handleQuickAdd}
              className={`absolute top-2 right-2 p-2 rounded-full transition-all duration-300 z-10 ${
                justAdded 
                  ? 'bg-green-500 text-white scale-110' 
                  : 'bg-solr-space-dark/80 text-white opacity-0 group-hover/image:opacity-100 hover:bg-solr-nebula-purple'
              }`}
              aria-label="Quick add to cart"
            >
              {justAdded ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>
          )}

          {/* Quick View Button - Hidden on mobile, visible on hover for larger screens */}
          <button
            onClick={handleQuickView}
            className="absolute inset-0 bg-black/60 hidden sm:flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-opacity duration-300"
          >
            <Eye className="w-6 h-6 md:w-8 md:h-8 text-white" />
            <span className="ml-2 text-white font-semibold text-sm md:text-base">Quick View</span>
          </button>
        </div>
        
        <div className="space-y-1.5 sm:space-y-2">
          <div className="text-[10px] sm:text-xs font-medium text-solr-nebula-purple">{product.tagline}</div>
          <h3 className="text-base sm:text-lg md:text-xl font-semibold line-clamp-1">{product.name}</h3>
          
          <Select
            value={String(selectedPack.size)}
            onValueChange={(value) => {
              const pack = packSizes.find(p => p.size === Number(value));
              if (pack) setSelectedPack(pack);
            }}
          >
            <SelectTrigger className="w-full mb-1.5 sm:mb-2 h-9 sm:h-10 text-sm">
              <SelectValue placeholder="Select pack size" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {packSizes.map((pack) => (
                <SelectItem key={pack.size} value={String(pack.size)}>
                  {pack.size} Pack
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex items-baseline justify-between flex-wrap gap-1">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-base sm:text-lg font-bold flex items-center">
                <PoundSterling className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5" />
                {selectedPack.subscription_price.toFixed(2)}
              </span>
              <span className="text-xs sm:text-sm text-solr-starfield-silver line-through flex items-center">
                <PoundSterling className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5" />
                {selectedPack.price.toFixed(2)}
              </span>
            </div>
            <span className="text-[10px] sm:text-xs text-solr-synapse-gold">Save 15%</span>
          </div>
          
          <p className="text-xs sm:text-sm text-solr-starfield-silver line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem]">
            {isPreorder && product.preorderMessage
              ? product.preorderMessage
              : `${product.description.substring(0, 100)}...`}
          </p>
          
          <div className="pt-3 sm:pt-4 flex flex-col space-y-2">
            <Button 
              onClick={handleAddToCart} 
              disabled={isOutOfStock}
              className={`w-full flex items-center justify-center text-xs sm:text-sm py-2 sm:py-2.5 ${
                isOutOfStock 
                  ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                  : isPreorder
                    ? 'bg-solr-nebula-purple hover:bg-solr-nebula-purple/90 text-white'
                    : 'btn-primary'
              }`}
            >
              {isPreorder ? <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> : <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />}
              {isOutOfStock ? 'Out of Stock' : isPreorder ? 'Preorder Now' : 'Add to Cart'}
            </Button>
            <Button
              variant="outline"
              className="w-full flex items-center justify-center text-xs sm:text-sm py-2 sm:py-2.5 border-solr-nebula-purple/60 text-solr-nebula-purple hover:bg-solr-nebula-purple/10 hover:text-solr-nebula-purple font-semibold"
            >
              View Full Details
              <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1" />
            </Button>
            <WishlistManager 
              variant="compact" 
              showAddToWishlist={true}
              productId={product.id}
              productName={product.name}
              productImage={product.image}
              productPrice={selectedPack.price}
            />
          </div>
        </div>
      </Link>
      
      <QuickViewModal
        product={product}
        open={showQuickView}
        onClose={() => setShowQuickView(false)}
      />
    </div>
  );
};

export default ProductCard;